import React, { FC } from 'react';
import styled from 'styled-components';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import OvCompactButton from '../atoms/OvCompactButton';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

type DropZoneVariant = 'area' | 'button';

interface OvVideoDropZoneProps {
  label: string;
  hint: string;
  limits: string;
  isMultiple: boolean;
  isDisabled: boolean;
  onFiles: (files: File[]) => void;
  buttonLabel?: string;
  variant?: DropZoneVariant;
  isButtonFocused?: boolean;
}

const ACCEPTED_FILES = {
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
};

const OvVideoDropZone: FC<OvVideoDropZoneProps> = ({
  label,
  hint,
  limits,
  isMultiple,
  isDisabled,
  onFiles,
  buttonLabel,
  variant = 'area',
  isButtonFocused = false,
}) => {
  const hasButton = !!buttonLabel;
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: ACCEPTED_FILES,
    multiple: isMultiple,
    disabled: isDisabled,
    noClick: hasButton,
    noKeyboard: hasButton,
    onDrop: (accepted, rejected) =>
      onFiles([...accepted, ...rejected.map(({ file }) => file)]),
  });

  const button = hasButton && (
    <OvCompactButton
      tone={variant === 'area' ? 'primary' : 'secondary'}
      autoFocus={isButtonFocused}
      disabled={isDisabled}
      onClick={open}
    >
      {buttonLabel}
    </OvCompactButton>
  );

  if (variant === 'button') {
    return (
      <span {...getRootProps()}>
        <input {...getInputProps()} aria-label={label} />
        {button}
      </span>
    );
  }

  return (
    <DropZone
      {...getRootProps()}
      $isActive={isDragActive}
      $isClickable={!hasButton}
    >
      <input {...getInputProps()} aria-label={label} />
      <CloudUploadIcon aria-hidden="true" />
      <span>{hint}</span>
      {button}
      <Hint>{limits}</Hint>
    </DropZone>
  );
};

export default OvVideoDropZone;

const DropZone = styled.div<{ $isActive: boolean; $isClickable: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1.5rem;
  border: 2px dashed
    ${({ $isActive }) =>
      $isActive ? Colours.OV_ACCENT_BLUE : Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.LARGE};
  text-align: center;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};
  color: ${Colours.OV_BASE};
`;

const Hint = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
